import { webApi } from '@mi-front/api-clients';

/**
 * Normalized KPI figures derived from `StreamQualityReportResponseDto.result.stats.kpis`.
 * Used to drive the stream quality KPI row without coupling the UI to the DTO shape.
 */
export type QualityKpiSnapshot = {
  /** Service health headline ratio from the API (0..1). */
  healthScoreRatio: number;
  /** Stations in scope for the report (denominator for affected-station ratios). */
  totalStations: number;
  /** Stations with at least one continuity or quality issue in the window. */
  stationsWithAnyIssueCount: number;
  /** Stations with at least one continuity incident. */
  continuityAffectedStationsCount: number;
  /** Stations with at least one quality incident. */
  qualityAffectedStationsCount: number;
  continuityIncidentCount: number;
  continuityMeanIncidentDurationSeconds: number;
  qualityTotalIncidentCount: number;
  /**
   * Mean quality incident duration in seconds.
   * `null` when there are no quality incidents (avoids division by zero).
   */
  qualityMeanIncidentDurationSeconds: number | null;
};

/**
 * Builds a KPI snapshot from the report payload, or `null` when global stats are missing.
 */
export function getQualityKpiSnapshotFromReport(
  report: webApi.StreamQualityReportResponseDto,
): QualityKpiSnapshot | null {
  const stats = report.result.stats;
  if (stats == null) {
    return null;
  }

  const { continuity, quality, serviceHealth } = stats.kpis;

  const qualityMeanIncidentDurationSeconds =
    quality.totalIncidentCount > 0 ? quality.totalDurationSeconds / quality.totalIncidentCount : null;

  return {
    healthScoreRatio: serviceHealth.healthScoreRatio,
    totalStations: stats.totalStations,
    stationsWithAnyIssueCount: stats.stationsWithAnyIssueCount,
    continuityAffectedStationsCount: continuity.affectedStationsCount,
    qualityAffectedStationsCount: quality.affectedStationsCount,
    continuityIncidentCount: continuity.incidentCount,
    continuityMeanIncidentDurationSeconds: continuity.meanIncidentDurationSeconds,
    qualityTotalIncidentCount: quality.totalIncidentCount,
    qualityMeanIncidentDurationSeconds,
  };
}
